import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ResourcesService, DailyInsightMessage } from "../api";
import { DailyMessageRenderer } from "../components/chat/DailyMessageRenderer";
import { ResearchReportRenderer } from "../components/chat/ResearchReportRenderer";

const DailyMessageDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [dailyMessage, setDailyMessage] = useState<DailyInsightMessage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchDailyMessage(id);
    }
  }, [id]);

  const fetchDailyMessage = async (messageId: string) => {
    setLoading(true);
    try {
      const data = await ResourcesService.dailyMessageRetrieve(messageId);
      setDailyMessage(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching daily message:", err);
      setError("Failed to load the daily message. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const capitalizeFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  if (loading) {
    return (
      <div className="scientia-container">
        <div className="loading-spinner-container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (error || !dailyMessage) {
    return (
      <div className="scientia-container">
        <div className="form-message is-error" role="alert">
          {error || "Daily message not found"}
        </div>
        <Link to="/resources" className="button is-outlined">
          ← Back to Resources
        </Link>
      </div>
    );
  }

  const hasValidResearchReports = dailyMessage.research_reports && dailyMessage.research_reports.length > 0 && dailyMessage.research_reports[0].report && dailyMessage.research_reports[0].report.body;

  return (
    <div className="scientia-container">
      <div className="daily-message-detail">
        <div className="daily-message-detail__header">
          <Link to="/resources" className="back-link">
            ← Back to Resources
          </Link>
          <h1 className="title">Daily Message</h1>
          <p className="subtitle">
            Delivered on {formatDate(dailyMessage.delivery_date)}
          </p>
        </div>

        <div className="scientia-card">
          <div className="scientia-card__content">
            <div className="daily-message-content">
              <DailyMessageRenderer messageText={dailyMessage.message_text} />
            </div>
          </div>
        </div>

        {hasValidResearchReports && (
          <div className="research-reports-section">
            <h2 className="section-title">Web Research</h2>
            {dailyMessage.research_reports.map((research) => (
              research.report && research.report.body && (
                <div key={research.id} className="scientia-card">
                  <div className="scientia-card__content">
                    <div className="research-report-header">
                      <h3 className="research-report-title">
                        🔍 {capitalizeFirstLetter(research.query)}
                      </h3>
                      <p className="research-report-date">
                        {formatDate(research.created)}
                      </p>
                    </div>
                    <div className="research-content">
                      <ResearchReportRenderer
                        reportData={JSON.stringify(research.report)}
                        context="standalone"
                      />
                    </div>
                  </div>
                </div>
              )
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyMessageDetailPage;
